/**
 * Apple Pay availability detection.
 *
 * Shared by CheckoutInfo (step 1 express button) and CheckoutPayment (step 3)
 * so the two cannot drift — they previously each carried their own copy of the
 * availability check and the WALLET_PAY_ENABLED flag.
 *
 * WHY THE SDK: window.ApplePaySession only exists in WebKit, so a plain
 * `typeof window.ApplePaySession !== 'undefined'` check limits Apple Pay to
 * Safari. Since iOS 18 (2024-09-16) Apple Pay also works in Chrome, Edge and
 * Firefox on desktop — the shopper scans a QR code and confirms on their
 * iPhone — but only if the page loads Apple's JS SDK. Loading it makes
 * ApplePaySession available in those browsers too.
 *
 * NOTE ON APPLE'S ACCEPTABLE USE GUIDELINES: Apple state that if you call
 * applePayCapabilities() and find the person has an active card in Wallet,
 * Apple Pay "should be the primary, but not necessarily sole, payment option"
 * on the page. Today it is the third radio behind Card and Finance. That is a
 * deliberate open question for Jon, not an oversight — raised 2026-09-25.
 */

// Opayo's Apple merchant identifier for the New Reg vendor account, as returned
// in the merchantIdentifier field of a direct-applepay-session.vsp response
// (captured from a live session on 2026-09-25). Not a secret — Apple's own flow
// hands it to the browser on every session. It is Opayo's, not ours, because we
// use the Opayo-managed certificate; if Opayo ever reissue it this must change.
export const APPLE_MERCHANT_IDENTIFIER =
  '742139845C3E310E06335D8BE9BFB37150B39CAEAA8135458BBEDFAF9AA06314';

const SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

let sdkPromise: Promise<void> | null = null;

/** Load Apple's JS SDK once. Resolves even on failure — callers fall back. */
export function loadApplePaySdk(): Promise<void> {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<void>((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }
    if (document.querySelector(`script[src="${SDK_URL}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    // Resolve on error too: Safari already has ApplePaySession natively, so a
    // blocked CDN must not stop Apple Pay working where it always did.
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return sdkPromise;
}

/** What we learned about this browser/device. */
export interface ApplePayAvailability {
  /** Show an Apple Pay button at all. */
  available: boolean;
  /**
   * The person has an active card provisioned in Wallet.
   * Apple's Acceptable Use Guidelines say Apple Pay should then be the PRIMARY
   * payment option — Jon confirmed 2026-09-25 he is happy with that — so this
   * is what drives ordering and pre-selection, not `available`.
   */
  hasCard: boolean;
}

/**
 * Should we show an Apple Pay button, and should it be primary?
 *
 * Prefers applePayCapabilities() (works in third-party browsers, and tells us
 * whether a card is actually provisioned) and falls back to canMakePayments()
 * on older Safari or if the capabilities call fails. Never throws.
 */
export async function getApplePayAvailability(): Promise<ApplePayAvailability> {
  const none: ApplePayAvailability = { available: false, hasCard: false };

  await loadApplePaySdk();

  const AP = (window as any).ApplePaySession;
  if (!AP) return none;

  if (typeof AP.applePayCapabilities === 'function') {
    try {
      const capabilities = await AP.applePayCapabilities(APPLE_MERCHANT_IDENTIFIER);
      const status = capabilities?.paymentCredentialStatus;
      // Show the button for every status except unsupported — a person can add
      // a card inside the sheet. Only 'paymentCredentialsAvailable' means a card
      // is already there, which is the bar Apple set for making it primary.
      return {
        available: status !== 'applePayUnsupported',
        hasCard: status === 'paymentCredentialsAvailable',
      };
    } catch (err) {
      console.warn('[NR Apple Pay] applePayCapabilities failed, falling back', err);
    }
  }

  try {
    // Legacy Safari path. canMakePayments() only says the device supports Apple
    // Pay, not that a card is provisioned, so we cannot claim hasCard here.
    const supported = typeof AP.canMakePayments === 'function' && AP.canMakePayments();
    return { available: !!supported, hasCard: false };
  } catch {
    return none;
  }
}
